import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import '../../../../features/cards/css/creator/feed.css';
import '../../../../assets/css/player.css';
import './ViewContent.css';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import {
  formatTimeAgo,
  formatDuration,
  isBundleContent,
  getMediaArray,
  getPrimaryMedia
} from '../../../../shared/utils/function';
import {
  DEFAULT_USER_AVATAR,
  MEDIA_TYPE,
  PLACEHOLDER_POST_IMAGE,
  ROLE,
  MESSAGE_READ
} from '../../../../shared/constants/common';
import BundleSlider from '../../../../features/slider/BundleSlider';
import LazyImage from '../../../../components/LazyImage/LazyImage';
import VideoPlayer from '../../../../shared/components/VideoPlayer'
import { useModalOpen } from '../../../../shared/utils/modalUtils.js';
import MediaGridDropdown from '../../../../features/cards/creator/MediaGridDropdown';


const ViewContent = ({
  post = null,
  creatorInfo = null,
  userRole = null,
  isDarkMode = true,
  showControls = false,
  isShowDuration = false,
  handleClosePostModal,
  posts = [], // Array of all posts
  currentIndex = 0, // Current post index
  onNavigate = null, // Navigation callback function
  onDeleted = null, // Post deletion callback function
  onUpdated = null, // Post update callback function
}) => {
  const navigate = useNavigation();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [bundleSlideIndex, setBundleSlideIndex] = useState(0);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const mediaContainerRef = useRef(null);

  // Reset video playing state and dropdown when post changes
  useEffect(() => {
    setIsVideoPlaying(false);
    setBundleSlideIndex(0);
    setOpenDropdownId(null);
  }, [post?._id]);

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [bundleSlideIndex]);

  // Handle dropdown toggle
  const onDropdownToggle = (postId) => {
    setOpenDropdownId(openDropdownId === postId ? null : postId);
  };

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(true);

  if (!post) return null;

  // Navigation handlers
  const handlePreviousPost = (e) => {
    e.stopPropagation();
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNextPost = (e) => {
    e.stopPropagation();
    if (currentIndex < posts.length - 1 && onNavigate) {
      onNavigate(currentIndex + 1);
    }
  };

  const canNavigatePrevious = currentIndex > 0;
  const canNavigateNext = currentIndex < posts.length - 1;

  const likes = post.tips || 0;
  const isBundle = isBundleContent(post);
  const mediaArray = getMediaArray(post);
  const primaryMedia = getPrimaryMedia(post);

  const activeMediaContext = isBundle
    ? (mediaArray[bundleSlideIndex] || primaryMedia)
    : primaryMedia;
  const isActiveVideo = activeMediaContext?.type_file === MEDIA_TYPE.VIDEO;
  const shouldShowVideoBadges = !isActiveVideo || !isVideoPlaying;

  const handleVideoPause = () => {
    const video = mediaContainerRef.current?.querySelector('video');
    if (video && !video.paused) return;
    setIsVideoPlaying(false);
  };

  const CaptionWithToggle = ({ text, maxLength = 100 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return null;

    const toggleReadMore = () => setIsExpanded(!isExpanded);

    return (
      <span className="caption-text">
        {isExpanded ? text : text.slice(0, maxLength) + (text.length > maxLength ? '...' : '')}
        {text.length > maxLength && (
          <span onClick={toggleReadMore} className="read-more-btn">
            {isExpanded ? MESSAGE_READ.SHOW_LESS_TEXT : MESSAGE_READ.SHOW_MORE_TEXT}
          </span>
        )}
      </span>
    );
  };

  const handleTagUser = (user) => {
    if (user?._id) {
      navigate.toChat(user._id);
    }
  };

  const modalContent = (
    <div className="post-modal-overlay cm_bg_blur">
      <div className="post-modal-content" >
        <div className={`modern-post-ui cm_modern_post_card ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
          <div className="cm_modern_post_content">

            <span onClick={handleClosePostModal} className="cm_modal_close_btn">
              <i className='fa fa-xmark'></i>
            </span>
            <span className="cm_timestamp_badge cm_timestamp_badge_in_modal">
              {formatTimeAgo(post.created_at)}
            </span>

            <div className="post_user_info">
              <LazyImage
                src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
                alt={creatorInfo?.username}
                className='cm-post-height'
                onError={(e) => {
                  e.target.src = DEFAULT_USER_AVATAR;
                }}
              />
            </div>

            <div className='cm_modern_post_text'>
              <div className="username-section">
                <h6>@{creatorInfo?.username}</h6>
              </div>
              {post.caption && (
                <div className="cm_post_caption">
                  <CaptionWithToggle
                    text={post.caption}
                    maxLength={100}
                  />
                </div>
              )}

              {post.tag_users && post.tag_users.length > 0 && (
                <div className="tagged-users-section">
                  {post.tag_users.map((user, index) => (
                    <span key={index} className="tag-user" onClick={() => handleTagUser(user)}>
                      @{user.username || user}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="post-content">
            <div className="post-media-container" ref={mediaContainerRef}>
              {shouldShowVideoBadges && (
                <>
                  <div className={`content-type-badge ${post.lock_content ? 'premium' : 'free'}`}>
                    {post.lock_content ? 'Premium' : 'Free'}
                  </div>

                  {post.lock_content && post.real_price && parseFloat(post.real_price) > 0 && (
                    <div className="price-badge">
                      ${parseFloat(post.real_price).toFixed(2)}
                    </div>
                  )}
                </>
              )}

              {isBundle && mediaArray && mediaArray.length > 0 ? (
                <BundleSlider
                  media={mediaArray}
                  showControls={showControls}
                  isShowDuration={isShowDuration}
                  onMessagingVideoPlayingChange={setIsVideoPlaying}
                  onCurrentIndexChange={setBundleSlideIndex}
                />
              ) : primaryMedia.type === MEDIA_TYPE.IMAGE ? (
                <LazyImage
                  src={primaryMedia.src}
                  alt="Post content"
                  className='cm-post-height post-media'
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_POST_IMAGE;
                  }}
                />
              ) : (
                <VideoPlayer
                  src={primaryMedia.src}
                  poster={primaryMedia.poster || null}
                  className='cm-post-height video-no-timeline bundle-slider-media'
                  showControls={showControls}
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  preventContextMenu
                  muted
                  loop
                  playsInline
                  fallbackText='Video unavailable'
                  showDurationBadge={true}
                  onPlayStarted={() => setIsVideoPlaying(true)}
                  onPause={handleVideoPause}
                />
              )}

              {/* Navigation Arrows */}
              {posts.length > 1 && (
                <>
                  {/* Left Arrow */}
                  {canNavigatePrevious && (
                    <button
                      className="post-navigation-arrow post-navigation-arrow-left"
                      onClick={handlePreviousPost}
                      aria-label="Previous post"
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                  )}

                  {/* Right Arrow */}
                  {canNavigateNext && (
                    <button
                      className="post-navigation-arrow post-navigation-arrow-right"
                      onClick={handleNextPost}
                      aria-label="Next post"
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  )}
                </>
              )}

              {/* Video duration badge for non-bundle videos */}
              {isShowDuration && post.type_file === MEDIA_TYPE.VIDEO && post.duration && shouldShowVideoBadges && (
                <div className={`video_time_badge`}>
                  <i className='fa-solid fa-video'></i>
                  {formatDuration(post.duration)}
                </div>
              )}
            </div>
          </div>

          <div className="post-actions">
            <div className="actions-left">
              <div className="actions-group">
                <div className="like-section">
                  <div className="like-icon">
                    <i className='fas fa-heart'></i>
                  </div>
                  <span className="like-count">
                    {likes.toLocaleString()}
                  </span>
                </div>

                <div className="tips-display">
                  <div className="tips-icon">
                    <i className="fa-solid fa-sack-dollar tips-icon-text"></i>
                  </div>
                  <span className="tips-amount color_green">
                    $ {post.total_amount ? parseFloat(post.total_amount).toFixed(2) : 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="actions-right">
              {userRole === ROLE.CREATOR_CONTENT && post?.isTrending && post?.isTrending === true && (
                <div className="trending-badge">
                  <span className="trending-badge-text">
                    Trending
                  </span>
                  <i className='fas fa-fire'></i>
                </div>
              )}

              <MediaGridDropdown
                className="icon-container view-content-creator-dropdown"
                iconClass="fa-ellipsis-vertical"
                post={post}
                userRole={userRole}
                isShowPin={false}
                isOpen={openDropdownId === (post._id || post.id)}
                onToggle={onDropdownToggle}
                onDeleted={() => {
                  onDeleted?.(post._id || post.id);
                  handleClosePostModal?.();
                }}
                onUpdated={(action, postId, newValue) => {
                  onUpdated?.(action, postId, newValue);
                  handleClosePostModal?.();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

export default ViewContent;
